import { SafePromise } from '@benzinga/safe-await';
import { IncludeHeader, proxyClientOptions, RestfulClient, Session } from '@benzinga/session';
import { NodeQueryParams, NodeQueryParamsTokenType, News } from '../entities';
import { BasicNewsEnvironment } from '../environment';
import { nodeContentRoutes } from './node-routes';

export class NodeContentRestful extends RestfulClient {
  constructor(session: Session, includeHeaders?: IncludeHeader) {
    const env = session.getEnvironment(BasicNewsEnvironment);
    super(env.nodeContentUrl, session, includeHeaders, proxyClientOptions());
  }

  fetchNodes = async (params: NodeQueryParams = {}): SafePromise<News[]> => {
    const token = this.session.getEnvironment(BasicNewsEnvironment).nodeContentKey;
    const path = this.routePath(nodeContentRoutes.list);
    const url = this.URL(path, {
      channels: params.channels ? params.channels.join(',') : undefined,
      date: params.date,
      dateFrom: params.dateFrom,
      dateTo: params.dateTo,
      displayOutput: params.displayOutput ?? 'headline',
      page: params.page ?? 0,
      pageSize: params.pageSize ?? 15,
      primaryTickers: params.primarySymbols ? params.primarySymbols.join(',') : undefined,
      sort: params.sort ?? 'updated:desc',
      tickers: params.primarySymbols ? undefined : params.symbols ? params.symbols.join(',') : undefined,
      topics: params.topics ? params.topics.join(',') : undefined,
      ...(this.usingProxy
        ? { tokenType: params.tokenType }
        : { token: params?.tokenType ? this.getToken(params.tokenType) : token }),
    });
    return this.get(url, {
      allowsAnonymousAuth: true,
      credentials: 'same-origin',
    });
  };

  getStory = async (id: number): SafePromise<News> => {
    const token = this.session.getEnvironment(BasicNewsEnvironment).nodeContentKey;
    const path = `${this.routePath(nodeContentRoutes.story)}/${id}`;
    const url = this.URL(path, this.withUpstreamCredentials({ displayOutput: 'full' }, { token }));
    return this.get(url, {
      allowsAnonymousAuth: true,
      credentials: 'same-origin',
    });
  };

  private getToken = (key?: NodeQueryParamsTokenType) => {
    const env = this.session.getEnvironment(BasicNewsEnvironment);
    switch (key) {
      case 'sec':
        return env.nodeContentSecKey;
      case 'partner':
        return env.partnerContentKey;
      case 'press-release':
        return env.pressReleaseKey;
      case 'korea':
        return env.koreaContentKey;
      case 'japan':
        return env.japanContentKey;
      case 'spain':
        return env.esContentKey;
      case 'italy':
        return env.itContentKey;
      case 'france':
        return env.frContentKey;
      default:
        return env.nodeContentKey;
    }
  };
}
